import type {
  AgentEvent,
  AgentProgress,
  ExecutionScope,
  MiddlewareTodo,
  TodoItem,
  UIBlock,
} from '../types/agent.progress.js';

export function createInitialAgentProgressState(rootScopeId: string): AgentProgress {
  return {
    status: 'idle',
    blocks: {},
    rootTimeline: [],
    scopes: {},
    rootScopeId,
    todos: [],
    lastSequenceApplied: 0,
  };
}

export function addBlock(state: AgentProgress, block: UIBlock): void {
  state.blocks[block.id] = block;
  state.activeBlockId = block.id;

  if (block.parentBlockId) {
    const parent = state.blocks[block.parentBlockId];
    if (parent) {
      parent.childBlockIds.push(block.id);
      parent.updatedAt = block.updatedAt;
      return;
    }
  }

  state.rootTimeline.push(block.id);
}

export function updateBlock(
  state: AgentProgress,
  blockId: string,
  updater: (block: UIBlock) => void,
  timestamp: number
): void {
  const block = state.blocks[blockId];
  if (!block) return;

  updater(block);
  block.updatedAt = timestamp;
}

export function upsertScope(state: AgentProgress, scope: ExecutionScope): void {
  state.scopes[scope.id] = scope;
}

export function updateScope(
  state: AgentProgress,
  scopeId: string,
  updater: (scope: ExecutionScope) => void
): void {
  const scope = state.scopes[scopeId];
  if (!scope) return;
  updater(scope);
}

function inferDepth(state: AgentProgress, parentBlockId: string | undefined): number {
  if (!parentBlockId) return 0;
  const parent = state.blocks[parentBlockId];
  if (!parent) return 0;
  return parent.depth + 1;
}

function reconcileTodos(previous: TodoItem[], nextTodos: MiddlewareTodo[], timestamp: number): TodoItem[] {
  const previousMap = new Map(previous.map((todo) => [todo.key, todo]));
  const counts = new Map<string, number>();

  return nextTodos.map((todo) => {
    const count = (counts.get(todo.content) ?? 0) + 1;
    counts.set(todo.content, count);

    const key = `${todo.content}::${count}`;
    const existing = previousMap.get(key);

    return {
      key,
      content: todo.content,
      status: todo.status,
      createdAt: existing?.createdAt ?? timestamp,
      updatedAt: timestamp,
    };
  });
}

export function reduceAgentEvent(state: AgentProgress, event: AgentEvent): AgentProgress {
  const next = structuredClone(state);

  switch (event.type) {
    case 'scope_started': {
      const blockId = String(event.payload.blockId);
      const parentBlockId = event.parentBlockId;

      upsertScope(next, {
        id: event.scopeId,
        kind: String(event.payload.scopeKind) as any,
        name: String(event.payload.name),
        parentScopeId:
          typeof event.payload.parentScopeId === 'string' ? event.payload.parentScopeId : undefined,
        parentBlockId,
        startedAt: event.timestamp,
        status: 'running',
      });

      addBlock(next, {
        id: blockId,
        kind: 'scope',
        createdAt: event.timestamp,
        updatedAt: event.timestamp,
        status: 'running',
        scopeId: event.scopeId,
        parentBlockId,
        childBlockIds: [],
        depth: inferDepth(next, parentBlockId),
        scopeKind: String(event.payload.scopeKind) as any,
        name: String(event.payload.name),
      });

      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'scope_completed': {
      updateScope(next, event.scopeId, (scope) => {
        scope.status = 'completed';
        scope.completedAt = event.timestamp;
      });

      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'scope') {
            block.status = 'completed';
          }
        },
        event.timestamp
      );

      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'scope_failed': {
      updateScope(next, event.scopeId, (scope) => {
        scope.status = 'error';
        scope.completedAt = event.timestamp;
      });

      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'scope') {
            block.status = 'error';
          }
        },
        event.timestamp
      );

      next.status = 'error';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'thinking_started': {
      addBlock(next, {
        id: String(event.payload.blockId),
        kind: 'thinking',
        createdAt: event.timestamp,
        updatedAt: event.timestamp,
        status: 'running',
        summary: typeof event.payload.summary === 'string' ? event.payload.summary : undefined,
        scopeId: event.scopeId,
        parentBlockId: event.parentBlockId,
        childBlockIds: [],
        depth: inferDepth(next, event.parentBlockId),
      });

      next.status = 'thinking';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'thinking_updated': {
      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'thinking') {
            block.summary = typeof event.payload.summary === 'string' ? event.payload.summary : block.summary;
            block.status = 'running';
          }
        },
        event.timestamp
      );

      next.status = 'thinking';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'thinking_completed': {
      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'thinking') {
            block.status = 'completed';
          }
        },
        event.timestamp
      );

      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'thought_stream_started': {
      addBlock(next, {
        id: String(event.payload.blockId),
        kind: 'thought_stream',
        createdAt: event.timestamp,
        updatedAt: event.timestamp,
        status: 'running',
        provider: typeof event.payload.provider === 'string' ? event.payload.provider : undefined,
        text: '',
        streaming: true,
        scopeId: event.scopeId,
        parentBlockId: event.parentBlockId,
        childBlockIds: [],
        depth: inferDepth(next, event.parentBlockId),
      });

      next.status = 'thinking';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'thought_stream_delta': {
      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'thought_stream') {
            block.text += String(event.payload.textDelta ?? '');
            block.streaming = true;
            block.status = 'running';
          }
        },
        event.timestamp
      );

      next.status = 'thinking';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'thought_stream_completed': {
      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'thought_stream') {
            block.streaming = false;
            block.status = 'completed';
          }
        },
        event.timestamp
      );

      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'tool_call_started': {
      addBlock(next, {
        id: String(event.payload.blockId),
        kind: 'tool_call',
        createdAt: event.timestamp,
        updatedAt: event.timestamp,
        status: 'running',
        toolCallId: String(event.payload.toolCallId),
        name: String(event.payload.name),
        input: event.payload.input,
        startedAt: event.timestamp,
        scopeId: event.scopeId,
        parentBlockId: event.parentBlockId,
        childBlockIds: [],
        depth: inferDepth(next, event.parentBlockId),
      });

      next.status = 'running_tool';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'tool_call_completed': {
      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'tool_call') {
            block.output = event.payload.output;
            block.completedAt = event.timestamp;
            block.status = 'completed';
          }
        },
        event.timestamp
      );

      next.status = 'thinking';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'tool_call_failed': {
      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'tool_call') {
            block.output = event.payload.error;
            block.completedAt = event.timestamp;
            block.status = 'error';
          }
        },
        event.timestamp
      );

      next.status = 'error';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'message_started': {
      addBlock(next, {
        id: String(event.payload.blockId),
        kind: 'message',
        createdAt: event.timestamp,
        updatedAt: event.timestamp,
        status: 'running',
        role: 'assistant',
        text: '',
        streaming: true,
        scopeId: event.scopeId,
        parentBlockId: event.parentBlockId,
        childBlockIds: [],
        depth: inferDepth(next, event.parentBlockId),
      });

      next.status = 'streaming';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'message_delta': {
      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'message') {
            block.text += String(event.payload.textDelta ?? '');
            block.streaming = true;
            block.status = 'running';
          }
        },
        event.timestamp
      );

      next.status = 'streaming';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'message_completed': {
      updateBlock(
        next,
        String(event.payload.blockId),
        (block) => {
          if (block.kind === 'message') {
            block.text = typeof event.payload.fullText === 'string' ? event.payload.fullText : block.text;
            block.streaming = false;
            block.status = 'completed';
          }
        },
        event.timestamp
      );

      next.status = 'done';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'todos_replaced': {
      const todos = Array.isArray(event.payload.todos) ? (event.payload.todos as MiddlewareTodo[]) : [];

      next.todos = reconcileTodos(next.todos, todos, event.timestamp);
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'query_failed': {
      addBlock(next, {
        id: String(event.payload.blockId),
        kind: 'error',
        createdAt: event.timestamp,
        updatedAt: event.timestamp,
        status: 'error',
        message: String(event.payload.message ?? 'Query failed'),
        details: event.payload.details,
        scopeId: event.scopeId,
        parentBlockId: event.parentBlockId,
        childBlockIds: [],
        depth: inferDepth(next, event.parentBlockId),
      });

      next.status = 'error';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    case 'query_completed': {
      next.status = 'done';
      next.lastSequenceApplied = event.sequence;
      return next;
    }

    default: {
      next.lastSequenceApplied = event.sequence;
      return next;
    }
  }
}
